import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Chip,
  CircularProgress,
  TablePagination,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

export const InvoicesTable = ({
  filteredInvoices,
  selectedInvoices,
  setSelectedInvoices,
  handleEditInvoice,
  formatAmount,
  formatDate,
  loading,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: "#f9fafb" }}>
            <TableCell padding="checkbox">
              <Checkbox
                checked={
                  selectedInvoices.length === filteredInvoices.length &&
                  filteredInvoices.length > 0
                }
                indeterminate={
                  selectedInvoices.length > 0 &&
                  selectedInvoices.length < filteredInvoices.length
                }
                onChange={(e) =>
                  setSelectedInvoices(
                    e.target.checked ? filteredInvoices.map((i) => i.id) : []
                  )
                }
              />
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>PO Number</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredInvoices.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                <Typography color="text.secondary">
                  No invoices found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredInvoices.map((invoice) => (
              <TableRow
                key={invoice.id}
                hover
                sx={{ "&:hover": { bgcolor: "#f9fafb" } }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedInvoices.includes(invoice.id)}
                    onChange={() =>
                      setSelectedInvoices((prev) =>
                        prev.includes(invoice.id)
                          ? prev.filter((id) => id !== invoice.id)
                          : [...prev, invoice.id]
                      )
                    }
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {invoice.po_number || `#${invoice.id}`}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {invoice.company_info?.name || "N/A"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatAmount(invoice.amount_due)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={invoice.status}
                    size="small"
                    color={
                      invoice.status === "paid"
                        ? "success"
                        : invoice.status === "approved"
                        ? "info"
                        : invoice.status === "canceled"
                        ? "default"
                        : "warning"
                    }
                    sx={{ fontWeight: 500, minWidth: 80 }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(invoice.created_at)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleEditInvoice(invoice)}
                    sx={{ color: "#6b7280" }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {totalCount > 0 && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50]}
        />
      )}
    </TableContainer>
  );
};

export const CollabsTable = ({
  filteredCollabs,
  selectedCollabs,
  setSelectedCollabs,
  handleEditCampaign,
  handleEditPartnership,
  formatAmount,
  formatDate,
  loading,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleEdit = (collab) => {
    if (collab.type === "Campaign") {
      handleEditCampaign(collab);
    } else {
      handleEditPartnership(collab);
    }
  };

  return (
    <TableContainer>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: "#f9fafb" }}>
            <TableCell padding="checkbox">
              <Checkbox
                checked={
                  selectedCollabs.length === filteredCollabs.length &&
                  filteredCollabs.length > 0
                }
                indeterminate={
                  selectedCollabs.length > 0 &&
                  selectedCollabs.length < filteredCollabs.length
                }
                onChange={(e) =>
                  setSelectedCollabs(
                    e.target.checked ? filteredCollabs.map((c) => c.id) : []
                  )
                }
              />
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Creator/Creators</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredCollabs.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                <Typography color="text.secondary">
                  No collabs found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredCollabs.map((collab) => (
              <TableRow
                key={`${collab.type}-${collab.id}`}
                hover
                sx={{ "&:hover": { bgcolor: "#f9fafb" } }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedCollabs.includes(collab.id)}
                    onChange={() =>
                      setSelectedCollabs((prev) =>
                        prev.includes(collab.id)
                          ? prev.filter((id) => id !== collab.id)
                          : [...prev, collab.id]
                      )
                    }
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={collab.type}
                    size="small"
                    variant="outlined"
                    color={collab.type === "Campaign" ? "primary" : "secondary"}
                    sx={{ fontWeight: 500 }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {collab.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {collab.type === "Campaign"
                      ? `${collab.creators?.length || 0} creators`
                      : collab.creator || "N/A"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={collab.status || collab.campaign_status}
                    size="small"
                    color={
                      (collab.status || collab.campaign_status) === "Launched" ||
                      (collab.status || collab.campaign_status) === "Active"
                        ? "success"
                        : "default"
                    }
                    sx={{ fontWeight: 500 }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatAmount(
                      collab.amount ||
                        collab.campaign_sum ||
                        collab.computed_total
                    )}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {collab.type === "Campaign"
                      ? `${collab.video_amount || 0} videos`
                      : `${collab.deliverables_count || 0} deliverables`}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(collab.proposal_date || collab.created_at)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(collab)}
                    sx={{ color: "#6b7280" }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {totalCount > 0 && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50]}
        />
      )}
    </TableContainer>
  );
};

export const CampaignsTable = ({
  filteredCampaigns,
  selectedProjects,
  setSelectedProjects,
  handleEditCampaign,
  formatAmount,
  formatDate,
  loading,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: "#f9fafb" }}>
            <TableCell padding="checkbox">
              <Checkbox
                checked={
                  selectedProjects.length === filteredCampaigns.length &&
                  filteredCampaigns.length > 0
                }
                indeterminate={
                  selectedProjects.length > 0 &&
                  selectedProjects.length < filteredCampaigns.length
                }
                onChange={(e) =>
                  setSelectedProjects(
                    e.target.checked ? filteredCampaigns.map((c) => c.id) : []
                  )
                }
              />
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Campaign Name</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Videos</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Creators</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredCampaigns.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                <Typography color="text.secondary">
                  No campaigns found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredCampaigns.map((campaign) => (
              <TableRow
                key={campaign.id}
                hover
                sx={{ "&:hover": { bgcolor: "#f9fafb" } }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedProjects.includes(campaign.id)}
                    onChange={() =>
                      setSelectedProjects((prev) =>
                        prev.includes(campaign.id)
                          ? prev.filter((id) => id !== campaign.id)
                          : [...prev, campaign.id]
                      )
                    }
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {campaign.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={campaign.campaign_status}
                    size="small"
                    sx={{ fontWeight: 500 }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatAmount(campaign.campaign_sum)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {campaign.video_amount || 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {campaign.creators?.length || 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(campaign.proposal_date)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleEditCampaign(campaign)}
                    sx={{ color: "#6b7280" }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const PartnershipsTable = ({
  filteredPartnerships,
  selectedPartnerships,
  setSelectedPartnerships,
  handleEditPartnership,
  formatAmount,
  formatDate,
  loading,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: "#f9fafb" }}>
            <TableCell padding="checkbox">
              <Checkbox
                checked={
                  selectedPartnerships.length ===
                    filteredPartnerships.length &&
                  filteredPartnerships.length > 0
                }
                indeterminate={
                  selectedPartnerships.length > 0 &&
                  selectedPartnerships.length < filteredPartnerships.length
                }
                onChange={(e) =>
                  setSelectedPartnerships(
                    e.target.checked
                      ? filteredPartnerships.map((p) => p.id)
                      : []
                  )
                }
              />
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Partnership</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Creator</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Deliverables</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredPartnerships.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                <Typography color="text.secondary">
                  No partnerships found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredPartnerships.map((partnership) => (
              <TableRow
                key={partnership.id}
                hover
                sx={{ "&:hover": { bgcolor: "#f9fafb" } }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedPartnerships.includes(partnership.id)}
                    onChange={() =>
                      setSelectedPartnerships((prev) =>
                        prev.includes(partnership.id)
                          ? prev.filter((id) => id !== partnership.id)
                          : [...prev, partnership.id]
                      )
                    }
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {partnership.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {partnership.creator}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={partnership.status}
                    size="small"
                    sx={{ fontWeight: 500 }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatAmount(partnership.computed_total)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {partnership.deliverables_count || 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(partnership.proposal_date)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleEditPartnership(partnership)}
                    sx={{ color: "#6b7280" }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const DeliverablesTable = ({
  filteredDeliverables,
  selectedDeliverables,
  setSelectedDeliverables,
  handleEditDeliverable,
  formatAmount,
  formatDate,
  loading,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: "#f9fafb" }}>
            <TableCell padding="checkbox">
              <Checkbox
                checked={
                  selectedDeliverables.length ===
                    filteredDeliverables.length &&
                  filteredDeliverables.length > 0
                }
                indeterminate={
                  selectedDeliverables.length > 0 &&
                  selectedDeliverables.length < filteredDeliverables.length
                }
                onChange={(e) =>
                  setSelectedDeliverables(
                    e.target.checked
                      ? filteredDeliverables.map((d) => d.id)
                      : []
                  )
                }
              />
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Partnership</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Creator</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredDeliverables.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                <Typography color="text.secondary">
                  No deliverables found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredDeliverables.map((deliverable) => (
              <TableRow
                key={deliverable.id}
                hover
                sx={{ "&:hover": { bgcolor: "#f9fafb" } }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedDeliverables.includes(deliverable.id)}
                    onChange={() =>
                      setSelectedDeliverables((prev) =>
                        prev.includes(deliverable.id)
                          ? prev.filter((id) => id !== deliverable.id)
                          : [...prev, deliverable.id]
                      )
                    }
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={deliverable.deliverable_type}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {deliverable.partnership?.name ||
                      `ID: ${deliverable.partnership_id}`}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {deliverable.creator_id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatAmount(deliverable.amount)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={deliverable.status}
                    size="small"
                    sx={{ fontWeight: 500 }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(deliverable.due_date)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleEditDeliverable(deliverable)}
                    sx={{ color: "#6b7280" }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {totalCount > 0 && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50]}
        />
      )}
    </TableContainer>
  );
};